import { Button, ButtonTheme } from 'components/Button';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { AUTHENTICATE, RESET_AUTH_STEP } from 'store/actions';
import { selectUserAuthStepTfaSetup } from 'store/auth/selectors';
import { formatDate } from 'lib/riskbase';
import { useAlert } from 'hooks/useAlert';
import { useConfirm } from 'hooks/useConfirm';
import { str } from 'lib/str';
import { API_DATE_FORMAT, UI_DATE_FORMAT_LONG } from 'lib/constants';
import { styles } from './styles';
import { UserChip } from './UserChip';
import { TfaSetupComponent } from './TfaSetupComponent';

export const TfaSetup: React.FC = () => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const confirm = useConfirm();
  const { uri, secret, required } = useSelector(selectUserAuthStepTfaSetup) || {
    uri: '',
    secret: '',
    required: null,
  };

  const handleCancel = () => dispatch(RESET_AUTH_STEP());

  const handleSubmit = (code: string) =>
    dispatch(AUTHENTICATE.request({ code }));

  const handleSkip = () => dispatch(AUTHENTICATE.request({ code: undefined }));

  const handleSkipPress = () =>
    required
      ? formatDate(required.date, API_DATE_FORMAT) >
        formatDate(new Date(), API_DATE_FORMAT)
        ? confirm({
            title: 'Multi-Factor Authentication Required',
            text:
              `You can skip setup until *${formatDate(
                required.date,
                UI_DATE_FORMAT_LONG,
              )}*\n\n` +
              `After ${formatDate(required.date, UI_DATE_FORMAT_LONG)} you${
                str.apos
              }ll be required to setup MFA to continue using RiskBase\n\n` +
              'If you skip setup now, you can still setup MFA on your Profile page in Settings',
            cancelText: 'Setup Now',
            confirmText: 'Skip Setup',
            destructive: true,
            onConfirm: handleSkip,
          })
        : alert({
            title: 'Multi-Factor Authentication Required',
            text: 'You can no longer skip setup. Please enable MFA on your account to continue using RiskBase',
          })
      : handleSkip();

  React.useEffect(() => {
    if (!secret) {
      dispatch(RESET_AUTH_STEP());
    }
  }, [dispatch, secret]);

  return (
    <>
      <UserChip />
      <View
        style={{
          shadowColor: 'black',
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.1,
          shadowRadius: 40,
          borderRadius: 8,
          marginBottom: theme.paddingVertical / 2,
          backgroundColor: theme.colors.background,
          flexShrink: 1,
        }}
      >
        <TfaSetupComponent
          secret={secret}
          uri={uri}
          required={required}
          onSubmit={(code) => handleSubmit(code)}
          withScrollView
        />
      </View>
      <View style={styles.buttonContainer}>
        <Button
          buttonTheme={ButtonTheme.secondary}
          onPress={handleCancel}
          text="Cancel Sign In"
          marginRight
        />
        <Button
          buttonTheme={ButtonTheme.themeless}
          onPress={handleSkipPress}
          text="Skip"
          marginRight
        />
      </View>
    </>
  );
};
